import React, { useState, useEffect } from 'react';
import { XMarkIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

const ChangeEmailModal = ({ isOpen, onClose, currentEmail, onSubmit, isLoading, serverError }) => {
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Clear the input when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewEmail('');
      setError('');
      setCopied(false);
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!newEmail) {
      setError('El campo de email es requerido');
      return;
    }

    if (newEmail === currentEmail) {
      setError('El nuevo email debe ser diferente del actual');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Formato de email inválido');
      return;
    }

    onSubmit(newEmail);
  };

  const handleClose = () => {
    setNewEmail('');
    setError('');
    setCopied(false);
    onClose();
  };

  const copyToClipboard = async () => {
    const textToCopy = serverError || error;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500"
              onClick={handleClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Cambiar Dirección de Email
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Current Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Actual
                  </label>
                  <input
                    type="text"
                    value={currentEmail || 'N/A'}
                    disabled
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* New Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nuevo Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Ingresa el nuevo email"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                    required
                  />
                </div>

                {/* Error Message */}
                {(error || serverError) && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex items-start justify-between">
                      <p className="text-sm text-red-800 flex-1">{serverError || error}</p>
                      <button
                        type="button"
                        onClick={copyToClipboard}
                        className="ml-3 flex-shrink-0 inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded border border-red-300 transition-colors"
                        title="Copiar mensaje"
                      >
                        {copied ? (
                          <>
                            <CheckIcon className="h-4 w-4 mr-1" />
                            Copiado
                          </>
                        ) : (
                          <>
                            <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                            Copiar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Warning */}
                <div className="rounded-md bg-yellow-50 p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Advertencia:</strong> Cambiar la dirección de email actualizará las
                    credenciales de inicio de sesión del usuario. El usuario necesitará usar el nuevo email para iniciar sesión.
                  </p>
                </div>

                {/* Actions */}
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Cambiando...' : 'Cambiar Email'}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeEmailModal;
